import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  BookOpen,
  Cpu,
  HelpCircle,
  KeyRound,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
  UserRound,
  Wrench,
} from 'lucide-react';
import PillTabs from '../../../../components/common/PillTabs';
import { llmGatewayApi } from '../../llm-gateway/llmGatewayApi';
import type { LlmGateway } from '../../llm-gateway/llmGatewayTypes';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import type { GatewayApiKey, QuotaAssignmentStatus } from '../governedMcpTypes';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import {
  fieldClass,
  ghostBtn,
  pageSlice,
  primaryBtn,
  selectField,
  tableHeadClass,
  TablePager,
  tdClass,
  thClass,
  trClass,
} from '../components/GatewayUi';
import ApiKeyFormDrawer from '../drawers/ApiKeyFormDrawer';
import ApiKeySetupHelpDrawer from '../drawers/ApiKeySetupHelpDrawer';
import InferenceKeyDialog, { type InferenceKeyTarget } from '../drawers/InferenceKeyDialog';
import LlmCallHelpDialog, { type LlmCallHelpTarget } from '../drawers/LlmCallHelpDialog';
import { llmInferenceBaseUrl } from '../governedMcpConstants';
import { clientLabel } from '../quotaUsage';

type KeySection = 'tools' | 'llm';
type KindFilter = 'all' | 'key' | 'user';
type StatusFilter = 'all' | 'active' | 'revoked';
type SortMode = 'name-asc' | 'name-desc';

const PAGE_SIZE = 12;

const KEY_SECTIONS = [
  { id: 'tools' as const, label: 'Tools', icon: Wrench },
  { id: 'llm' as const, label: 'LLM Gateway', icon: Cpu },
];

const KIND_TABS: { id: KindFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'key', label: 'Keys' },
  { id: 'user', label: 'Users' },
];

function assignmentStatusTone(status: QuotaAssignmentStatus, isLight: boolean): string {
  if (status === 'inactive') return isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-800 text-neutral-400';
  return isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300';
}

function Menu({
  open,
  onClose,
  children,
  isLight,
  align = 'right',
  root,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  isLight: boolean;
  align?: 'left' | 'right';
  root: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(`[data-menu-root="${root}"]`)) return;
      onClose();
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open, onClose, root]);
  if (!open) return null;
  return (
    <div
      className={`absolute z-20 mt-1.5 min-w-[200px] rounded-xl border py-1 shadow-xl ${align === 'left' ? 'left-0' : 'right-0'} ${
        isLight ? 'bg-white border-neutral-200 shadow-neutral-900/10' : 'bg-neutral-900 border-neutral-700'
      }`}
    >
      {children}
    </div>
  );
}

function KeyCard({
  item,
  tag,
  isLight,
  onOpen,
  onCopyJwt,
  onHelp,
  onEdit,
  onDelete,
}: {
  item: GatewayApiKey;
  tag: string;
  isLight: boolean;
  onOpen: () => void;
  onCopyJwt: () => void;
  onHelp: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const revoked = item.status === 'revoked';
  const UserIcon = item.kind === 'user' ? UserRound : KeyRound;

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className={`group relative flex flex-col rounded-2xl p-5 text-left cursor-pointer transition-all ${
        isLight
          ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.04] hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)] hover:-translate-y-0.5'
          : 'bg-neutral-900 ring-1 ring-white/10 hover:ring-white/20'
      } ${revoked ? 'opacity-70' : ''}`}
    >
      <div className="flex items-center gap-3 min-w-0 pr-16">
        <span
          className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
            item.kind === 'user'
              ? isLight
                ? 'bg-sky-50 text-sky-600'
                : 'bg-sky-500/15 text-sky-300'
              : isLight
                ? 'bg-indigo-50 text-indigo-600'
                : 'bg-indigo-500/15 text-indigo-300'
          }`}
        >
          <UserIcon size={18} />
        </span>
        <h3 className={`text-[15px] font-semibold tracking-tight truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>
          {item.name}
        </h3>
      </div>

      <p className={`mt-3 text-sm leading-relaxed line-clamp-2 min-h-[2.5rem] ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
        {item.description || item.username || item.email || 'No description provided.'}
      </p>

      <div className={`mt-4 pt-4 border-t ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
            isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-neutral-300'
          }`}
        >
          {tag}
        </span>
      </div>

      <div
        className={`absolute top-3.5 right-3.5 flex items-center gap-0.5 rounded-xl p-0.5 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto transition-opacity ${
          isLight ? 'bg-white/90 shadow-sm ring-1 ring-black/5' : 'bg-neutral-800 ring-1 ring-white/10'
        }`}
      >
        <button
          type="button"
          className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-indigo-600 hover:bg-indigo-50' : 'text-neutral-400 hover:text-white hover:bg-neutral-700'}`}
          aria-label="Copy JWT"
          onClick={(event) => {
            event.stopPropagation();
            onCopyJwt();
          }}
        >
          <Copy size={14} />
        </button>
        <button
          type="button"
          className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-indigo-600 hover:bg-indigo-50' : 'text-neutral-400 hover:text-white hover:bg-neutral-700'}`}
          aria-label="Setup help"
          onClick={(event) => {
            event.stopPropagation();
            onHelp();
          }}
        >
          <HelpCircle size={14} />
        </button>
        <button
          type="button"
          className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-indigo-600 hover:bg-indigo-50' : 'text-neutral-400 hover:text-white hover:bg-neutral-700'}`}
          aria-label="Edit"
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-red-600 hover:bg-red-50' : 'text-neutral-400 hover:text-red-400 hover:bg-neutral-700'}`}
          aria-label="Delete"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </article>
  );
}

function LlmGatewayKeysPanel({
  isLight,
  onToast,
}: {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [gateways, setGateways] = useState<LlmGateway[]>([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(false);
  const [gatewaysError, setGatewaysError] = useState<string | null>(null);
  const [inferenceTarget, setInferenceTarget] = useState<InferenceKeyTarget | null>(null);
  const [callHelpTarget, setCallHelpTarget] = useState<LlmCallHelpTarget | null>(null);

  useEffect(() => {
    let cancelled = false;
    setGatewaysLoading(true);
    setGatewaysError(null);
    logGovernedMcp('app', 'api_keys_llm_gateways_load');
    llmGatewayApi
      .listGateways()
      .then((items) => {
        if (cancelled) return;
        setGateways(items);
        logGovernedMcp('app', 'api_keys_llm_gateways_loaded', { count: items.length });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load LLM gateways.';
        setGatewaysError(message);
        logGovernedMcp('error', 'api_keys_llm_gateways_failed', { message });
      })
      .finally(() => {
        if (!cancelled) setGatewaysLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return gateways
      .map((item) => ({
        id: item.id,
        name: item.name || item.id,
        publicAlias: item.publicAlias || '',
        llmType: item.llmType,
        routeKey: item.routeKey || '',
        status: item.status,
        hasInferenceKey: Boolean(item.hasInferenceKey),
        inferenceKeyLast4: item.inferenceKeyLast4,
      }))
      .filter((item) => {
        if (!q) return true;
        const callUrl = item.routeKey ? llmInferenceBaseUrl(item.routeKey) : '';
        return `${item.name} ${item.publicAlias} ${callUrl} ${item.status}`.toLowerCase().includes(q);
      })
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [gateways, query]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const paged = pageSlice(rows, page);
  const copyUrl = async (url: string, quotaId: string) => {
    if (!url) {
      onToast('error', 'Call URL is empty');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      onToast('success', 'Call URL copied');
      logGovernedMcp('app', 'inference_url_copied', { quotaId, source: 'api_keys' });
    } catch {
      onToast('error', 'Could not copy to clipboard');
      logGovernedMcp('warning', 'inference_url_copy_failed', { quotaId, source: 'api_keys' });
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search size={15} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
        <input
          className={`${fieldClass(isLight)} pl-10 rounded-full ${isLight ? 'bg-white' : ''}`}
          placeholder="Search LLM gateway keys…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {gatewaysError ? <p className="text-xs text-red-500">{gatewaysError}</p> : null}
      {gatewaysLoading && !rows.length ? (
        <p className={`text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Loading LLM gateways…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          isLight={isLight}
          title="No LLM gateways"
          description="Create an LLM gateway first, then issue the key here. The plaintext is shown once."
        />
      ) : (
        <>
          <div
            className={`overflow-auto min-h-[280px] rounded-2xl border shadow-sm ${
              isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-900'
            }`}
          >
            <table className="w-full min-w-[1100px] text-sm">
              <thead className={tableHeadClass(isLight)}>
                <tr>
                  <th className={thClass()}>LLM gateway</th>
                  <th className={thClass()}>Call URL</th>
                  <th className={thClass()}>Key</th>
                  <th className={thClass()}> </th>
                </tr>
              </thead>
              <tbody>
                {paged.map((item) => {
                  const callUrl = item.routeKey ? llmInferenceBaseUrl(item.routeKey) : '';
                  const canIssue = item.status === 'active' && Boolean(item.routeKey);
                  const issueTitle = item.status !== 'active'
                    ? 'Activate this LLM gateway before issuing a key'
                    : item.routeKey
                      ? item.hasInferenceKey
                        ? 'Reissue inference key'
                        : 'Issue inference key'
                      : 'Save the gateway to mint a call URL';
                  return (
                    <tr key={item.id} className={trClass(isLight)}>
                      <td className={tdClass()}>
                        <p className={`font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.name}</p>
                        {item.publicAlias ? (
                          <p className={`text-xs mt-0.5 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.publicAlias}</p>
                        ) : null}
                      </td>
                      <td className={tdClass()}>
                        {callUrl ? (
                          <div className="flex items-center gap-1 min-w-0">
                            <code className={`text-[11px] truncate max-w-[240px] ${isLight ? 'text-neutral-600' : 'text-neutral-300'}`}>
                              {callUrl}
                            </code>
                            <button
                              type="button"
                              className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-400 hover:text-indigo-600 hover:bg-indigo-50' : 'text-neutral-500 hover:text-white hover:bg-neutral-700'}`}
                              aria-label="Copy call URL"
                              title="Copy call URL"
                              onClick={() => void copyUrl(callUrl, item.id)}
                            >
                              <Copy size={14} />
                            </button>
                          </div>
                        ) : (
                          <span className={isLight ? 'text-neutral-400' : 'text-neutral-500'}>Save the gateway to mint a URL</span>
                        )}
                      </td>
                      <td className={tdClass()}>
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${assignmentStatusTone(item.status === 'active' ? 'active' : 'inactive', isLight)}`}>
                            {item.status === 'active' ? 'Active' : item.status === 'draft' ? 'Draft' : 'Disabled'}
                          </span>
                          {item.hasInferenceKey ? (
                            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300'}`}>
                              Key {item.inferenceKeyLast4 ? `••••${item.inferenceKeyLast4}` : 'issued'}
                            </span>
                          ) : (
                            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-800 text-neutral-400'}`}>
                              Not issued
                            </span>
                          )}
                        </span>
                      </td>
                      <td className={`${tdClass()} text-right`}>
                        <div className="inline-flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            className={`${ghostBtn(isLight)} !h-8 !text-xs`}
                            disabled={!item.routeKey}
                            title={item.routeKey ? 'How to call this gateway' : 'Save the gateway to mint a call URL'}
                            onClick={() => {
                              setCallHelpTarget({
                                id: item.id,
                                name: item.name,
                                publicAlias: item.publicAlias,
                                llmType: item.llmType,
                                routeKey: item.routeKey,
                                hasInferenceKey: item.hasInferenceKey,
                                inferenceKeyLast4: item.inferenceKeyLast4,
                              });
                              logGovernedMcp('app', 'inference_call_help_open', {
                                gatewayId: item.id,
                                source: 'api_keys',
                              });
                            }}
                          >
                            <BookOpen size={13} />
                            How to call
                          </button>
                          <button
                            type="button"
                            className={`${item.hasInferenceKey ? ghostBtn(isLight) : primaryBtn()} !h-8 !text-xs`}
                            disabled={!canIssue}
                            title={issueTitle}
                            onClick={() => {
                              setInferenceTarget({
                                id: item.id,
                                name: item.name,
                                publicAlias: item.publicAlias,
                                llmType: item.llmType,
                                routeKey: item.routeKey,
                                hasInferenceKey: item.hasInferenceKey,
                                inferenceKeyLast4: item.inferenceKeyLast4,
                                source: 'gateway',
                              });
                              logGovernedMcp('app', 'inference_key_dialog_open', {
                                gatewayId: item.id,
                                source: 'api_keys',
                              });
                            }}
                          >
                            <KeyRound size={13} />
                            {item.hasInferenceKey ? 'Reissue key' : 'Issue key'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <TablePager page={page} total={rows.length} isLight={isLight} onPage={setPage} noun="gateway" />
        </>
      )}
      <InferenceKeyDialog
        target={inferenceTarget}
        isLight={isLight}
        onClose={() => setInferenceTarget(null)}
        onToast={onToast}
        onIssued={({ last4 }) => {
          if (!inferenceTarget) return;
          setGateways((current) =>
            current.map((item) =>
              item.id === inferenceTarget.id ? { ...item, hasInferenceKey: true, inferenceKeyLast4: last4 } : item
            )
          );
        }}
      />
      <LlmCallHelpDialog
        target={callHelpTarget}
        isLight={isLight}
        onClose={() => setCallHelpTarget(null)}
        onToast={onToast}
      />
    </div>
  );
}

export default function ApiKeysTab({
  isLight,
  onToast,
}: {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, saveApiKey, deleteApiKey } = useGovernedMcp();
  const [section, setSection] = useState<KeySection>('tools');
  const [query, setQuery] = useState('');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sort, setSort] = useState<SortMode>('name-asc');
  const [page, setPage] = useState(1);
  const [kindOpen, setKindOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [editing, setEditing] = useState<GatewayApiKey | null | 'new'>(null);
  const [deleteTarget, setDeleteTarget] = useState<GatewayApiKey | null>(null);
  const [helpTarget, setHelpTarget] = useState<GatewayApiKey | null>(null);

  const toolKeys = useMemo(() => state.apiKeys.filter((item) => item.kind !== 'llm'), [state.apiKeys]);
  const inKind = useMemo(
    () => toolKeys.filter((item) => (kindFilter === 'all' ? true : item.kind === kindFilter)),
    [toolKeys, kindFilter]
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = inKind.filter((item) => {
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (clientFilter !== 'all' && item.clientId !== clientFilter) return false;
      if (!q) return true;
      const client = clientLabel(item.clientId, state.clients);
      return `${item.name} ${item.username} ${item.email} ${item.userId} ${item.tenantId} ${item.details} ${item.description} ${client}`
        .toLowerCase()
        .includes(q);
    });
    return [...filtered].sort((a, b) => {
      const cmp = a.name.localeCompare(b.name);
      return sort === 'name-desc' ? -cmp : cmp;
    });
  }, [inKind, query, statusFilter, clientFilter, sort, state.clients]);

  useEffect(() => {
    setPage(1);
  }, [query, kindFilter, statusFilter, clientFilter, sort]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageStart = rows.length ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const pageEnd = Math.min(safePage * PAGE_SIZE, rows.length);
  const pageItems = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const filterLabel = statusFilter === 'all' ? 'All statuses' : statusFilter === 'active' ? 'Active' : 'Revoked';
  const kindLabel = KIND_TABS.find((tab) => tab.id === kindFilter)?.label || 'All';
  const kindCount = (id: KindFilter) =>
    id === 'all' ? toolKeys.length : toolKeys.filter((item) => item.kind === id).length;

  const copy = async (text: string, label: string) => {
    if (!text) {
      onToast('error', `${label} is empty`);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      onToast('success', `${label} copied`);
      logGovernedMcp('app', 'api_key_copied', { label });
    } catch {
      onToast('error', 'Could not copy to clipboard');
      logGovernedMcp('warning', 'api_key_copy_failed', { label });
    }
  };

  const openCreate = () => {
    setEditing('new');
    logGovernedMcp('app', 'api_key_create_open');
  };

  const menuItem = (active: boolean) =>
    `w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3 ${
      active
        ? isLight
          ? 'bg-indigo-50 text-indigo-700'
          : 'bg-indigo-500/15 text-indigo-300'
        : isLight
          ? 'text-neutral-700 hover:bg-neutral-50'
          : 'text-neutral-300 hover:bg-neutral-800'
    }`;

  const pillBtn = (active?: boolean) =>
    `inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm font-medium border transition-colors ${
      active
        ? isLight
          ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
          : 'border-indigo-500/30 bg-indigo-500/15 text-indigo-300'
        : isLight
          ? 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50'
          : 'border-neutral-700 bg-neutral-900 text-neutral-300 hover:bg-neutral-800'
    }`;

  return (
    <div className="space-y-5">
      <PillTabs
        isLight={isLight}
        ariaLabel="API key type"
        value={section}
        items={KEY_SECTIONS}
        onChange={(next) => {
          setSection(next);
          logGovernedMcp('app', 'api_key_section', { section: next });
        }}
      />
      {section === 'llm' ? (
        <LlmGatewayKeysPanel isLight={isLight} onToast={onToast} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={15} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
              <input
                className={`${fieldClass(isLight)} pl-10 rounded-full ${isLight ? 'bg-white' : ''}`}
                placeholder="Search API keys…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="relative" data-menu-root="key-kind">
              <button type="button" className={pillBtn(kindFilter !== 'all')} onClick={() => setKindOpen((value) => !value)}>
                {kindLabel}
                <span className="tabular-nums text-[11px] opacity-70">{kindCount(kindFilter)}</span>
                <ChevronDown size={13} />
              </button>
              <Menu open={kindOpen} onClose={() => setKindOpen(false)} isLight={isLight} align="left" root="key-kind">
                {KIND_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={menuItem(kindFilter === tab.id)}
                    onClick={() => {
                      setKindFilter(tab.id);
                      setKindOpen(false);
                      logGovernedMcp('app', 'api_key_kind_tab', { kind: tab.id });
                    }}
                  >
                    {tab.label}
                    <span className="tabular-nums text-[11px] opacity-60">{kindCount(tab.id)}</span>
                  </button>
                ))}
              </Menu>
            </div>
            <select
              className={`${selectField(isLight)} w-auto rounded-full`}
              value={clientFilter}
              onChange={(event) => {
                setClientFilter(event.target.value);
                logGovernedMcp('app', 'api_key_client_filter', { clientId: event.target.value });
              }}
            >
              <option value="all">All clients</option>
              {state.clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
            <div className="relative" data-menu-root="key-filters">
              <button type="button" className={pillBtn(statusFilter !== 'all')} onClick={() => setFiltersOpen((value) => !value)}>
                <SlidersHorizontal size={14} />
                {filterLabel}
                <ChevronDown size={13} />
              </button>
              <Menu open={filtersOpen} onClose={() => setFiltersOpen(false)} isLight={isLight} align="left" root="key-filters">
                {(['all', 'active', 'revoked'] as StatusFilter[]).map((status) => (
                  <button
                    key={status}
                    type="button"
                    className={menuItem(statusFilter === status)}
                    onClick={() => {
                      setStatusFilter(status);
                      setFiltersOpen(false);
                      logGovernedMcp('app', 'api_key_status_filter', { status });
                    }}
                  >
                    {status === 'all' ? 'All statuses' : status === 'active' ? 'Active' : 'Revoked'}
                  </button>
                ))}
              </Menu>
            </div>
            <div className={`inline-flex items-center gap-1 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              <span className="tabular-nums px-1">
                {pageStart}–{pageEnd} of {rows.length}
              </span>
              <button
                type="button"
                className={pillBtn()}
                disabled={safePage <= 1}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                type="button"
                className={pillBtn()}
                disabled={safePage >= pageCount}
                onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
                aria-label="Next page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
            <div className="relative ml-auto" data-menu-root="key-sort">
              <button type="button" className={pillBtn()} onClick={() => setSortOpen((value) => !value)}>
                {sort === 'name-desc' ? 'Name Z–A' : 'Name A–Z'}
                <ChevronDown size={13} />
              </button>
              <Menu open={sortOpen} onClose={() => setSortOpen(false)} isLight={isLight} root="key-sort">
                <button
                  type="button"
                  className={menuItem(sort === 'name-asc')}
                  onClick={() => {
                    setSort('name-asc');
                    setSortOpen(false);
                  }}
                >
                  Name A–Z
                </button>
                <button
                  type="button"
                  className={menuItem(sort === 'name-desc')}
                  onClick={() => {
                    setSort('name-desc');
                    setSortOpen(false);
                  }}
                >
                  Name Z–A
                </button>
              </Menu>
            </div>
            <button type="button" className={`${primaryBtn()} rounded-full`} onClick={openCreate}>
              <Plus size={14} /> Add
            </button>
          </div>

          {rows.length === 0 ? (
            <EmptyState
              isLight={isLight}
              title={toolKeys.length === 0 ? 'No API keys' : 'No keys in this view'}
              description="Issue one JWT per user per registered client. Reissue from the existing key — do not create a second card."
              action={
                <button type="button" className={`${primaryBtn()} rounded-full`} onClick={openCreate}>
                  <Plus size={14} /> Add API key
                </button>
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {pageItems.map((item) => (
                <KeyCard
                  key={item.id}
                  item={item}
                  tag={item.clientId ? clientLabel(item.clientId, state.clients) : 'Unassigned client'}
                  isLight={isLight}
                  onOpen={() => setEditing(item)}
                  onCopyJwt={() => void copy(item.secret, 'JWT')}
                  onHelp={() => setHelpTarget(item)}
                  onEdit={() => setEditing(item)}
                  onDelete={() => setDeleteTarget(item)}
                />
              ))}
            </div>
          )}

          <ApiKeyFormDrawer
            open={editing !== null}
            initial={editing && editing !== 'new' ? editing : null}
            isLight={isLight}
            onToast={onToast}
            onClose={() => setEditing(null)}
            onSave={(item) => {
              const result = saveApiKey(item);
              if (!result.ok) {
                onToast('error', result.reason);
                return result;
              }
              setEditing(null);
              onToast('success', `Key “${item.name}” saved`);
              return result;
            }}
          />
          <ApiKeySetupHelpDrawer
            open={Boolean(helpTarget)}
            item={helpTarget}
            endpoint={helpTarget ? state.endpoints.find((entry) => entry.id === helpTarget.endpointId) || null : null}
            boundClient={helpTarget ? state.clients.find((entry) => entry.id === helpTarget.clientId) || null : null}
            isLight={isLight}
            onToast={onToast}
            onClose={() => setHelpTarget(null)}
          />
          <ConfirmDialog
            open={Boolean(deleteTarget)}
            title="Delete API key?"
            description={
              deleteTarget ? `Remove “${deleteTarget.name}”? This credential can no longer authenticate to the gateway.` : ''
            }
            confirmLabel="Delete"
            danger
            isLight={isLight}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={() => {
              if (deleteTarget) {
                deleteApiKey(deleteTarget.id);
                onToast('success', 'API key removed');
              }
              setDeleteTarget(null);
            }}
          />
        </>
      )}
    </div>
  );
}
